import { useState, useEffect, useRef } from 'react';
import { useConfig } from '../../context/ConfigContext';
import { useLanguage } from '../../context/LanguageContext';
import { PARTS, CATEGORIES } from '../../data/parts';
import { generateImage, GENERATION_MESSAGES } from '../../services/apiService';
import styles from './BikeViewer.module.css';

// Import 360 images
const TOTAL_FRAMES = 28;

export default function BikeViewer({ activeCategory }) {
    const { config } = useConfig();
    const { t } = useLanguage();
    const [is360Mode, setIs360Mode] = useState(false);
    const [currentFrame, setCurrentFrame] = useState(0);
    const [isDragging, setIsDragging] = useState(false);
    const startX = useRef(0);

    // AI Generation State
    const [isGenerating, setIsGenerating] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState('');
    const [generatedImage, setGeneratedImage] = useState(null);
    const [geminiPrompt, setGeminiPrompt] = useState(''); // Store the real prompt

    // Find current view point based on active category
    const currentCategory = CATEGORIES.find(c => c.id === activeCategory) || CATEGORIES[0];
    const viewPoint = currentCategory.viewPoint || { x: 0, y: 0, scale: 1 };

    // 获取选中的车身颜色 (使用新的 body 分类)
    const selectedColor = PARTS.body?.find(p => p.id === config.body);

    // Helper to get 360 frame path
    const get360FramePath = (colorCode, frameIndex) => {
        const paddedIndex = String(frameIndex + 1).padStart(2, '0');
        return `/assets/360/2026-fat-boy-${colorCode}-motorcycle/2026-fat-boy-${colorCode}-motorcycle-${paddedIndex}.jpg`;
    };

    // 获取颜色代码用于360图片路径
    const getColorCode = () => {
        // 基于颜色 ID 映射到图片目录代码
        const colorCodeMap = {
            'dark_billiard_gray': 'm85',
            'vivid_black': 'm04',
            'white_onyx_pearl': 'm07',
            'brilliant_red': 'm44',
            'olive_steel_metallic_vivid_black': 'm78'
        };
        return colorCodeMap[config.body] || 'm85';
    };

    // Construct image path based on configuration
    const [displayImage, setDisplayImage] = useState('');

    useEffect(() => {
        // Reset generated image when config changes
        setGeneratedImage(null);
        setIsGenerating(false);
        setGeminiPrompt('');

        // 获取选中的舒适配件和风格配件
        const selectedComfort = config.comfort || [];
        const selectedStyle = config.style || [];
        const hasAccessories = selectedComfort.length > 0 || selectedStyle.length > 0;

        // 检查是否有配件图层
        if (selectedComfort.length === 1 && selectedStyle.length === 0) {
            const comfortPart = PARTS.comfort?.find(p => p.id === selectedComfort[0]);
            if (comfortPart && comfortPart.layerImage) {
                setDisplayImage(comfortPart.layerImage);
                return;
            }
        }

        // 基础配置 - 显示车身颜色图片
        const isStockConfig = (
            config.performance === 'none' &&
            !hasAccessories
        );

        if (isStockConfig && selectedColor?.image) {
            setDisplayImage(selectedColor.image);
            return;
        }

        // 有配件选装 - 尝试生成或使用基础图片
        if (selectedColor?.image) {
            setDisplayImage(selectedColor.image);
        } else {
            // 回退到默认图片
            setDisplayImage('/assets/360/2026-fat-boy-m78-motorcycle/2026-fat-boy-m78-motorcycle-01.jpg');
        }

    }, [config, selectedColor]);

    // Preload 360 images for current color
    useEffect(() => {
        const colorCode = getColorCode();
        Array.from({ length: TOTAL_FRAMES }).forEach((_, i) => {
            const img = new Image();
            img.src = get360FramePath(colorCode, i);
        });
    }, [config.body]);

    const handleImageError = (e) => {
        // 回退到默认图片
        const fallbackPath = '/assets/360/2026-fat-boy-m78-motorcycle/2026-fat-boy-m78-motorcycle-01.jpg';
        if (displayImage !== fallbackPath) {
            setDisplayImage(fallbackPath);
        } else {
            e.target.style.display = 'none';
            if (e.target.nextSibling) {
                e.target.nextSibling.style.display = 'flex';
            }
        }
    };

    const handleMouseDown = (e) => {
        if (!is360Mode) return;
        setIsDragging(true);
        startX.current = e.clientX;
    };

    const handleMouseMove = (e) => {
        if (!is360Mode || !isDragging) return;
        const delta = e.clientX - startX.current;
        if (Math.abs(delta) > 20) {
            const direction = delta > 0 ? -1 : 1;
            setCurrentFrame(prev => {
                let next = prev + direction;
                if (next < 0) next = TOTAL_FRAMES - 1;
                if (next >= TOTAL_FRAMES) next = 0;
                return next;
            });
            startX.current = e.clientX;
        }
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    return (
        <div
            className={styles.viewer}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onTouchStart={(e) => handleMouseDown(e.touches[0])}
            onTouchMove={(e) => handleMouseMove(e.touches[0])}
            onTouchEnd={handleMouseUp}
        >
            <div className={styles.stage}>
                {/* AI Loading Overlay */}
                {isGenerating && (
                    <div className={styles.loadingOverlay}>
                        <div className={styles.spinner}></div>
                        <div className={styles.loadingText}>{loadingMessage}</div>
                    </div>
                )}

                <div
                    className={styles.imageContainer}
                    style={{
                        transform: is360Mode ? 'none' : `scale(${viewPoint.scale}) translate(${viewPoint.x}%, ${viewPoint.y}%)`,
                        cursor: is360Mode ? 'grab' : 'default'
                    }}
                >
                    <img
                        src={is360Mode ? get360FramePath(getColorCode(), currentFrame) : displayImage}
                        alt="Harley Davidson Fat Boy"
                        className={styles.bikeImage}
                        draggable="false"
                        onError={handleImageError}
                    />
                    <div className={styles.placeholder} style={{ display: 'none' }}>
                        <div className={styles.placeholderText}>
                            NO IMAGE AVAILABLE<br />
                            {is360Mode ? `Angle ${currentFrame}` : config.body}
                        </div>
                    </div>
                </div>

                {/* Controls */}
                <div className={styles.controls}>
                    <button
                        className={`${styles.controlBtn} ${is360Mode ? styles.active : ''}`}
                        onClick={() => setIs360Mode(!is360Mode)}
                    >
                        {is360Mode ? 'EXIT 360°' : '360° VIEW'}
                    </button>
                </div>

                {is360Mode && (
                    <div className={styles.instruction}>
                        Drag to Rotate
                    </div>
                )}
            </div>
        </div>
    );
}
